import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { logger } from '../../lib/logger';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@pantry/ui';
import {
  useHousehold,
  useUpdateHousehold,
  useResetTour,
  OFF_COUNTRIES,
  EXPIRY_THRESHOLD_PRESETS,
  DEFAULT_EXPIRY_THRESHOLD_DAYS,
  type HouseholdRole,
} from '@pantry/api-client';
import { SUPPORTED_LANGUAGES } from '@pantry/shared';
import { translatePresetLabel } from '@pantry/i18n';
import { toast } from 'sonner';
import {
  ArrowLeftIcon,
  HomeIcon,
  TrashIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  LanguageIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/useAuth';
import { MemberList } from '../../components/households';
import { DeleteHouseholdDialog } from '../../components/households/DeleteHouseholdDialog';
import { useTourContext } from '../../components/onboarding/useTourContext';

export const Route = createFileRoute('/_app/households_/$householdId')({
  component: HouseholdSettingsPage,
});

function HouseholdSettingsPage() {
  const { t, i18n } = useLingui();
  const { householdId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startTour, setFirstHouseholdId } = useTourContext();
  const resetTour = useResetTour();

  const { data: household, isLoading, error } = useHousehold(householdId);
  const updateHousehold = useUpdateHousehold();

  // Get current user's role in this household
  const currentUserId = user?.id || '';
  const currentMember = household?.members.find((m) => m.userId === currentUserId);
  const currentUserRole: HouseholdRole = currentMember?.role || 'member';

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isStartingSettingsMiniTour, setIsStartingSettingsMiniTour] = useState(false);

  const handleStartSettingsMiniTour = async () => {
    if (isStartingSettingsMiniTour) return;

    setIsStartingSettingsMiniTour(true);
    try {
      await resetTour.mutateAsync({ tourId: 'households-settings-basics', deviceType: 'web' });
      await startTour('households-settings-basics');
    } catch (tourError) {
      console.error('Failed to start household settings mini tour:', tourError);
    } finally {
      setIsStartingSettingsMiniTour(false);
    }
  };

  // Auto-save handler for name changes (debounced via onBlur)
  const handleNameSave = async (value: string) => {
    if (!household || value === household.name) return;

    try {
      await updateHousehold.mutateAsync({
        id: householdId,
        data: { name: value },
      });
      toast.success(t`Household name updated`);
    } catch (err) {
      logger.error('Failed to update household name', err, { householdId });
      const message = err instanceof Error ? err.message : t`Failed to update household name`;
      toast.error(message);
    }
  };

  // Auto-save handler for country changes
  const handleCountryChange = async (value: string) => {
    if (!household || value === household.country) return;

    try {
      await updateHousehold.mutateAsync({
        id: householdId,
        data: { country: value },
      });
      toast.success(t`Region updated`);
    } catch (err) {
      logger.error('Failed to update region', err, { householdId });
      const message = err instanceof Error ? err.message : t`Failed to update region`;
      toast.error(message);
    }
  };

  // Auto-save handler for language changes
  const handleLanguageChange = async (value: string) => {
    const newLanguage = value || undefined;
    if (!household || newLanguage === household.language) return;

    try {
      await updateHousehold.mutateAsync({
        id: householdId,
        data: { language: newLanguage },
      });
      toast.success(t`Default language updated`);
    } catch (err) {
      logger.error('Failed to update language', err, { householdId });
      const message = err instanceof Error ? err.message : t`Failed to update language`;
      toast.error(message);
    }
  };

  // Controlled state for expiry selects (reverts on mutation failure)
  const [expiryThreshold, setExpiryThreshold] = useState(
    household?.expiryThresholdDays ?? DEFAULT_EXPIRY_THRESHOLD_DAYS
  );
  const [dashboardExpiry, setDashboardExpiry] = useState(household?.dashboardExpiryDays ?? 7);

  useEffect(() => {
    if (household) {
      setExpiryThreshold(household.expiryThresholdDays);
      setDashboardExpiry(household.dashboardExpiryDays);
    }
  }, [household?.expiryThresholdDays, household?.dashboardExpiryDays]);

  // Auto-save handler for expiry threshold changes
  const handleExpiryThresholdChange = async (value: string) => {
    const days = parseInt(value, 10);
    if (!household || isNaN(days) || days === household.expiryThresholdDays) return;

    setExpiryThreshold(days);
    try {
      await updateHousehold.mutateAsync({
        id: householdId,
        data: { expiryThresholdDays: days },
      });
      toast.success(t`Expiry alert threshold updated`);
    } catch (err) {
      setExpiryThreshold(household.expiryThresholdDays);
      logger.error('Failed to update expiry threshold', err, { householdId });
      const message = err instanceof Error ? err.message : t`Failed to update expiry threshold`;
      toast.error(message);
    }
  };

  // Auto-save handler for dashboard expiry window changes
  const handleDashboardExpiryChange = async (value: string) => {
    const days = parseInt(value, 10);
    if (!household || isNaN(days) || days === household.dashboardExpiryDays) return;

    setDashboardExpiry(days);
    try {
      await updateHousehold.mutateAsync({
        id: householdId,
        data: { dashboardExpiryDays: days },
      });
      toast.success(t`Dashboard expiry window updated`);
    } catch (err) {
      setDashboardExpiry(household.dashboardExpiryDays);
      logger.error('Failed to update dashboard expiry window', err, { householdId });
      const message =
        err instanceof Error ? err.message : t`Failed to update dashboard expiry window`;
      toast.error(message);
    }
  };

  const handleDeleteSuccess = () => {
    navigate({ to: '/households' });
  };

  useEffect(() => {
    setFirstHouseholdId(householdId);
  }, [householdId, setFirstHouseholdId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link
          to="/households"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to Households</Trans>
        </Link>
        <Card className="rounded-xl">
          <CardContent className="py-8">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                <ExclamationTriangleIcon className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-destructive font-medium">
                {error.message || t`Failed to load household`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!household) {
    return null;
  }

  // Only owner can delete the household
  const isOwner = currentUserRole === 'owner';
  const canDelete = isOwner;

  return (
    <div className="space-y-6" data-tour="household-settings-page">
      {/* Breadcrumb */}
      <div>
        <Link
          to="/households"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to Households</Trans>
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-4 items-center">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10">
            <Cog6ToothIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans>Household Settings</Trans>
            </h1>
            <p className="text-muted-foreground">{household.name}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => void handleStartSettingsMiniTour()}
          data-tour="households-settings-help-basics"
          aria-label={t`Start household settings basics mini tour`}
          title={t`Household settings basics help`}
          aria-busy={isStartingSettingsMiniTour}
          disabled={isStartingSettingsMiniTour}
        >
          <QuestionMarkCircleIcon className="h-5 w-5" />
        </Button>
      </div>

      {/* General Settings */}
      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <HomeIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>General Settings</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Name */}
          <div className="space-y-2" data-tour="settings-name">
            <Label htmlFor="household-name">
              <Trans>Household Name</Trans>
            </Label>
            <Input
              id="household-name"
              type="text"
              defaultValue={household.name}
              onBlur={(e) => handleNameSave(e.target.value)}
              placeholder={t`My Home`}
              maxLength={100}
              disabled={updateHousehold.isPending}
              className="rounded-xl"
            />
            <p className="text-xs text-muted-foreground">
              <Trans>Give your household a name that everyone will recognize.</Trans>
            </p>
          </div>

          {/* Country */}
          <div className="space-y-2" data-tour="settings-region">
            <Label htmlFor="household-country" className="flex items-center gap-2">
              <GlobeAltIcon className="h-4 w-4" />
              <Trans>Region</Trans>
            </Label>
            <select
              id="household-country"
              defaultValue={household.country || 'world'}
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={updateHousehold.isPending}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border disabled:cursor-not-allowed disabled:opacity-50"
            >
              {OFF_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              <Trans>
                Select your region to get more relevant product search results from Open Food Facts.
              </Trans>
            </p>
          </div>

          {/* Language */}
          <div className="space-y-2" data-tour="settings-language">
            <Label htmlFor="household-language" className="flex items-center gap-2">
              <LanguageIcon className="h-4 w-4" />
              <Trans>Default Language</Trans>
            </Label>
            <select
              id="household-language"
              defaultValue={household.language || ''}
              onChange={(e) => handleLanguageChange(e.target.value)}
              disabled={updateHousehold.isPending}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">{t`Use region default`}</option>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              <Trans>
                Set a default language for product names in this household. Members can override
                this in their personal settings.
              </Trans>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Expiry Settings */}
      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <ClockIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Expiry Settings</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Expiry Alert Threshold */}
          <div className="space-y-2">
            <Label htmlFor="expiry-threshold">
              <Trans>Expiry Alert Threshold</Trans>
            </Label>
            <select
              id="expiry-threshold"
              value={expiryThreshold}
              onChange={(e) => handleExpiryThresholdChange(e.target.value)}
              disabled={updateHousehold.isPending}
              aria-label={t`Expiry alert threshold`}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border disabled:cursor-not-allowed disabled:opacity-50"
            >
              {EXPIRY_THRESHOLD_PRESETS.map((preset) => (
                <option key={preset.days} value={preset.days}>
                  {translatePresetLabel(i18n, preset.label)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              <Trans>
                Items expiring within this many days will be flagged as &quot;expiring soon&quot;.
              </Trans>
            </p>
          </div>

          {/* Dashboard Expiry Window */}
          <div className="space-y-2">
            <Label htmlFor="dashboard-expiry">
              <Trans>Dashboard Expiry Window</Trans>
            </Label>
            <select
              id="dashboard-expiry"
              value={dashboardExpiry}
              onChange={(e) => handleDashboardExpiryChange(e.target.value)}
              disabled={updateHousehold.isPending}
              aria-label={t`Dashboard expiry window`}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border disabled:cursor-not-allowed disabled:opacity-50"
            >
              {EXPIRY_THRESHOLD_PRESETS.map((preset) => (
                <option key={preset.days} value={preset.days}>
                  {translatePresetLabel(i18n, preset.label)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              <Trans>The dashboard will show items expiring within this many days.</Trans>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Members Section */}
      <MemberList
        householdId={householdId}
        householdName={household.name}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
      />

      {/* Danger Zone */}
      {canDelete && (
        <Card className="rounded-xl border-destructive/30 bg-destructive/5" data-tour="danger-zone">
          <CardHeader>
            <CardTitle className="font-display text-destructive flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/10">
                <ExclamationTriangleIcon className="h-4 w-4 text-destructive" />
              </div>
              <Trans>Danger Zone</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-medium text-foreground">
                  <Trans>Delete Household</Trans>
                </p>
                <p className="text-sm text-muted-foreground">
                  <Trans>
                    Permanently delete this household and all its data. This action cannot be
                    undone.
                  </Trans>
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                className="rounded-xl text-destructive border-destructive/50 hover:bg-destructive/10 hover:border-destructive"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                <Trans>Delete Household</Trans>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteHouseholdDialog
        householdId={householdId}
        householdName={household.name}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}
